import React from 'react';
import { styled } from '@compiled/react';
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Heading from '@atlaskit/heading';
import PageHeader from '@atlassian/jira-common-components-page-header';
// eslint-disable-next-line import/order
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { getNextgenSettingsUrl } from '@atlassian/jira-project-settings-apps-common/src/urls';
import { routeGroupsProjectSettingsServicedeskCustomerPermissionsRoute } from '@atlassian/jira-router-routes-project-settings-product-routes-service-desk-routes';
import { getProjectSettingsUrl } from '@atlassian/jira-servicedesk-common/src/navigation/settings';
import { JsmPageInteractiveEvent } from '@atlassian/jira-servicedesk-common/src/utils/browser-metric/index.tsx';
// eslint-disable-next-line import/order
import { useQueryParam, generatePath } from '@atlassian/react-resource-router';

import { portalSettingsPageLoad } from '../utils/performance-analytics';
import CustomerPortalForms from './customer-portal-forms';
import CustomizeHelpCenter from './customize-help-center';
import messages from './messages';
import PortalDetails from './portal-details';

type Props = {
	isNextGen: boolean;
	isOpenAccess: boolean;
	isAdmin: boolean;
	canAgentsManageHelpCenterAnnouncement: boolean;
	canAgentsManagePortalAnnouncement: boolean;
	canAdministerJira?: boolean;
	projectId: number;
	projectKey: string;
	projectName: string;
	name: string;
	baseUrl: string;
	description: string;
	portalLogoUrl: string;
	showPageHeader?: boolean;
	portalUrl: string;
	helpCenterUrl: string;
};

const JIRA_SUPPORT_LINK = 'https://support.atlassian.com/contact/';

const parseUrl = (url: string): URL | undefined => {
	try {
		if (!url) {
			return undefined;
		}
		return new URL(url);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'customerPortalChannels',
				packageName: 'jiraServicedeskSettingsCustomerPortalV2',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		return undefined;
	}
};

export const CustomerPortalSettingsPage = (props: Props) => {
	const {
		baseUrl,
		isNextGen,
		projectId,
		projectKey,
		projectName,
		isOpenAccess,
		isAdmin,
		description,
		portalLogoUrl,
		canAgentsManagePortalAnnouncement,
		canAgentsManageHelpCenterAnnouncement,
		canAdministerJira,
		name,
		showPageHeader = true,
		portalUrl,
		helpCenterUrl,
	} = props;

	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const [referrer] = useQueryParam('referrer');

	(!parseUrl(portalUrl) || !parseUrl(helpCenterUrl)) &&
		showFlag({
			type: 'error',
			title: messages.invalidUrlTitle,
			description: messages.invalidUrlDescription,
			isAutoDismiss: true,
			actions: [
				{
					content: messages.invalidUrlAction,
					href: JIRA_SUPPORT_LINK,
					target: '_blank',
				},
			],
		});

	const getPortalShortLink = () => {
		const portalShortLinkUrl = parseUrl(portalUrl);
		if (portalShortLinkUrl) {
			portalShortLinkUrl.searchParams.append('customize', 'true');
			return portalShortLinkUrl.toString();
		}
		return '';
	};

	const breadcrumbs = !isNextGen ? (
		// Replace with lodash/noop
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		<Breadcrumbs onExpand={() => {}}>
			<BreadcrumbsItem text="Projects" href="/jira/projects" />
			<BreadcrumbsItem text={projectName} href={`/browse/${projectKey}`} />
			<BreadcrumbsItem text="Project settings" href={getProjectSettingsUrl(projectKey)} />
		</Breadcrumbs>
	) : (
		<Breadcrumbs />
	);

	const customerPermissionsLink =
		isNextGen && !fg('update_jsm_project_settings_pages_urls')
			? `${getNextgenSettingsUrl(SERVICE_DESK_PROJECT, projectKey)}/channels/customer-permissions`
			: generatePath(routeGroupsProjectSettingsServicedeskCustomerPermissionsRoute.path, {
					projectKey,
				});

	return (
		<Container>
			<ContextualAnalyticsData
				containerType="project"
				containerId={projectId.toString()}
				attributes={{
					context: 'CustomerPortalSettings',
					projectId,
					...(referrer && {
						referrer,
					}),
				}}
				sourceName="viewCustomerPortalSettings"
				sourceType={SCREEN}
			>
				{
					// Due to jsd-request-types-redesign classic portal settings (maybe next gen in future)
					// will be placed within a tab container that will provide the header
					showPageHeader && (
						<PageHeader breadcrumbs={breadcrumbs}>
							{formatMessage(isNextGen ? messages.pageHeader : messages.classicPageHeader)}
						</PageHeader>
					)
				}
				<Heading as="h2" size="small">
					{formatMessage(messages.portalDetails)}
				</Heading>
				<PortalDetails
					portalUrlLink={portalUrl}
					customerPermissionsLink={customerPermissionsLink}
					portalShortLink={getPortalShortLink()}
					projectId={projectId}
					isNextGen={isNextGen}
					isOpenAccess={isOpenAccess}
					canAdministerJira={!!canAdministerJira}
				/>
				<CustomerPortalForms
					canAdministerJira={!!canAdministerJira}
					canAgentsManageHelpCenterAnnouncement={canAgentsManageHelpCenterAnnouncement}
					canAgentsManagePortalAnnouncement={canAgentsManagePortalAnnouncement}
					customerPermissionsLink={customerPermissionsLink}
					baseUrl={baseUrl}
					isAdmin={isAdmin}
					isNextGen={isNextGen}
					isOpenAccess={isOpenAccess}
					portalLogoUrl={portalLogoUrl}
					portalShortLink={getPortalShortLink()}
					projectId={projectId}
					projectKey={projectKey}
					startDesc={description}
					startName={name}
				/>
				{!ff('multi_help_center_kosh_integration') ? (
					<CustomizeHelpCenter
						canAdministerJira={!!canAdministerJira}
						helpCenterUrl={parseUrl(helpCenterUrl)}
					/>
				) : null}
				<JsmPageInteractiveEvent
					metricKey="servicedesk.portal-settings"
					// @ts-expect-error - TS2322 - Type 'PageLoadMetric' is not assignable to type 'BM3Metric'.
					metric={portalSettingsPageLoad}
				/>
				<FireScreenAnalytics />
			</ContextualAnalyticsData>
		</Container>
	);
};

export default CustomerPortalSettingsPage;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	margin: '0 auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 118}px`,
});
